import {produce} from 'immer';
import {ActionTypes} from './actions';

// Simplified state - only UI concerns
// Case/Juror data now lives in Dexie (see src/db/)
function getInitialState() {
  return {
    // Session/Auth
    sessionData: null,
    oldSessionData: null,
    
    // Loading states (for UI feedback during sync)
    caseListLoading: false,
    caseListError: null,
    casesErrors: {},
    jurorsErrors: {},
    
    // Modal state
    editingJuror: null,
    editingSeatNumber: null,
    focusOnEmoji: null,
    focusOnOccurrence: null,
    previewingJuror: false,
    editingCase: null,
    
    // UI preferences
    trackedBounds: {},
    poolSort: {}
  }
}

// Helper function exported for spacer cleanup (used by other components)
export function cleanUpSpacers(spacers, juryBoxSize) {
  juryBoxSize = parseInt(juryBoxSize);
  let spacersSorted = spacers.sort((a, b) => parseInt(a) - parseInt(b));

  let usedSpacers = 0;
  for (let i = 0; i < juryBoxSize + spacersSorted.length; i++) {
    let spaceNumber = (i + 1);
    let spacerIndex = spacersSorted.indexOf(spaceNumber);
    if (spacerIndex >= 0) {
      usedSpacers++;
    }
    let seatNumber = spaceNumber - usedSpacers;
    if (seatNumber >= juryBoxSize) {
      usedSpacers--;
    }
    if (seatNumber >= juryBoxSize) {
      break;
    }
  }

  return spacersSorted.filter((oneSpacer) => oneSpacer <= (juryBoxSize + usedSpacers));
}

export default produce((draft, action) => {
  switch(action.type) {
    // Session actions
    case ActionTypes.sessionCreated:
      if (draft.oldSessionData && draft.oldSessionData.email === action.payload.data.email) {
        draft.caseListError = false;
        draft.caseListLoading = false;
        draft.sessionData = action.payload.data;
      }
      else {
        return {
          ...getInitialState(),
          sessionData: action.payload.data
        };
      }
      break;
    case ActionTypes.updateSessionDataPoint:
      if (draft.sessionData) {
        draft.sessionData[action.payload.name] = action.payload.value;
      }
      break;
    case ActionTypes.sessionEnded:
      let oldSessionData = null;
      if (draft.sessionData && draft.sessionData.email) {
        oldSessionData = {
          email: draft.sessionData.email
        };
      }
      if (action.payload.clearAllData) {
        return {
          ...getInitialState(),
          oldSessionData
        };
      }
      else {
        draft.sessionData = null;
        draft.oldSessionData = oldSessionData;
      }
      break;

    // Loading state actions (for UI feedback)
    case ActionTypes.listCases:
      draft.caseListLoading = true;
      draft.caseListError = null;
      break;
    case ActionTypes.listCasesSuccess:
      draft.caseListLoading = false;
      break;
    case ActionTypes.listCasesFailure:
      draft.caseListLoading = false;
      if (action.payload.error) {
        draft.caseListError = {
          type: 'list',
          canDismiss: true,
          error: action.payload.error
        };
      }
      break;
    case ActionTypes.removeCaseListError:
      draft.caseListError = null;
      break;
    case ActionTypes.removeCaseError:
      delete draft.casesErrors[action.payload.caseId];
      break;
    case ActionTypes.removeJurorError:
      delete draft.jurorsErrors[action.payload.jurorId];
      break;

    // Case modal actions
    case ActionTypes.openCaseModal:
      draft.editingCase = action.payload.caseId;
      break;
    case ActionTypes.closeCaseModal:
      draft.editingCase = null;
      break;

    // Juror modal actions
    case ActionTypes.openJurorModal: {
      // Strip material-table's `tableData` bookkeeping field before this
      // object enters Redux state. material-table mutates `tableData`
      // in place on the row objects it's handed (see JuryPool.jsx), and
      // Immer's autoFreeze (on by default, including in production; this
      // app never calls setAutoFreeze(false)) recursively freezes whatever
      // we store here. If the SAME row instance is still referenced by
      // material-table's own internal data, its later attempt to write
      // `rowData.tableData.editing = mode` throws "Cannot add property
      // editing, object is not extensible" (Sentry JURYBOX-WEB-APP-1).
      const { tableData, ...jurorRest } = action.payload.juror || {};
      draft.editingJuror = jurorRest;
      draft.editingSeatNumber = action.payload.seatNumber;
      draft.focusOnEmoji = action.payload.focusOn;
      draft.focusOnOccurrence = action.payload.focusAfter;
      break;
    }
    case ActionTypes.previewJurorStart:
      draft.previewingJuror = true;
      break;
    case ActionTypes.previewJurorEnd:
      draft.previewingJuror = false;
      break;
    case ActionTypes.closeJurorModal:
      draft.editingJuror = null;
      draft.editingSeatNumber = null;
      draft.previewingJuror = false;
      draft.focusOnEmoji = null;
      draft.focusOnOccurrence = null;
      break;
    case ActionTypes.updateEditingJuror:
      if (draft.editingJuror) {
        for (let change of action.payload.changes) {
          draft.editingJuror[change.name] = change.value;
        }
      }
      break;

    // UI preference actions
    case ActionTypes.updateBounds:
      draft.trackedBounds[action.payload.name] = action.payload.bounds;
      break;
    case ActionTypes.updatePoolSort:
      if (!draft.poolSort[action.payload.caseId]) {
        draft.poolSort[action.payload.caseId] = {};
      }
      if (action.payload.columnNumber === -1) {
        draft.poolSort[action.payload.caseId] = {};
      }
      else {
        draft.poolSort[action.payload.caseId][action.payload.columnNumber] = action.payload.sortingDirection;
      }
      break;

    default:
      // nothing
      break;
  }
}, getInitialState());
